/**
 * Client-side API layer for calling the server-side SDK proxy
 * All calls go through Cloudflare Functions for consistent behavior across environments
 */
import type { BdSdk } from "@blockdaemon/bd-sdk";
import { HTTPError, TimeoutError } from "ky";
import { httpClient } from "../../shared/lib/http-client";
import type { GetAssetPriceResponse } from "./types";

interface SDKRequest {
  method: string;
  args?: unknown[];
  namespace?: string;
}

interface PendingRequest {
  promise: Promise<unknown>;
  timestamp: number;
}

class SDKAPIError extends Error {
  constructor(
    message: string,
    public status?: number,
    public details?: Record<string, unknown>,
    public originalError?: unknown,
  ) {
    super(message);
    this.name = "SDKAPIError";
  }

  /**
   * Get a detailed error message including context information
   */
  getDetailedMessage(): string {
    const baseMessage = this.message;

    if (!this.details) {
      return baseMessage;
    }

    const contextParts: string[] = [];

    if (this.details.method) {
      const namespace = this.details.namespace ? `${this.details.namespace}.` : "";
      contextParts.push(`Method: ${namespace}${this.details.method}`);
    }

    if (this.details.errorType && this.details.errorType !== "Error") {
      contextParts.push(`Type: ${this.details.errorType}`);
    }

    if (this.status) {
      contextParts.push(`Status: ${this.status}`);
    }

    if (contextParts.length > 0) {
      return `${baseMessage} (${contextParts.join(", ")})`;
    }

    return baseMessage;
  }

  /**
   * Get the full error information for debugging
   */
  getDebugInfo() {
    return {
      message: this.message,
      status: this.status,
      details: this.details,
      stack: this.stack,
      originalError: this.originalError,
    };
  }
}

// Request deduplication storage
const pendingRequests = new Map<string, PendingRequest>();

/**
 * Custom JSON serializer that handles BigInt values by converting them to strings
 */
function serializeBigInt(_key: string, value: unknown): unknown {
  if (typeof value === "bigint") {
    return value.toString();
  }
  return value;
}

/**
 * Create a unique key for request deduplication
 */
function createRequestKey(request: SDKRequest): string {
  return JSON.stringify(request, serializeBigInt);
}

/**
 * Check if a request is already in flight and still fresh
 */
function getExistingRequest<T>(requestKey: string): Promise<T> | null {
  const existing = pendingRequests.get(requestKey);
  if (!existing) return null;

  const now = Date.now();
  const age = now - existing.timestamp;

  // Consider request stale after 5 seconds for freshness
  if (age > 5000) {
    pendingRequests.delete(requestKey);
    return null;
  }

  return existing.promise as Promise<T>;
}

/**
 * Make a request to the SDK proxy endpoint with request deduplication and enhanced error handling
 */
async function callSDK<T = unknown>(request: SDKRequest): Promise<T> {
  try {
    // Check for existing request (deduplication)
    const requestKey = createRequestKey(request);
    const existingRequest = getExistingRequest<T>(requestKey);

    if (existingRequest) {
      // Reuse existing request instead of making a new one
      return await existingRequest;
    }

    // Create new request with ky using custom BigInt serialization
    const requestPromise = httpClient
      .post("/api/sdk", {
        body: JSON.stringify(request, serializeBigInt),
      })
      .json<T>();

    // Store pending request for deduplication
    pendingRequests.set(requestKey, {
      promise: requestPromise,
      timestamp: Date.now(),
    });

    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Clean up pending request
      pendingRequests.delete(requestKey);
    }
  } catch (error) {
    // Enhanced error handling with ky error types
    if (error instanceof HTTPError) {
      const errorData = await error.response.json().catch(() => ({}));

      // Log detailed error information for debugging
      console.error("SDK HTTP Error:", {
        status: error.response.status,
        statusText: error.response.statusText,
        url: error.response.url,
        errorData,
        request,
      });

      throw new SDKAPIError(
        errorData.error || `HTTP ${error.response.status}: ${error.response.statusText}`,
        error.response.status,
        {
          ...errorData.details,
          url: error.response.url,
          method: request.method,
          namespace: request.namespace,
        },
        error,
      );
    }

    if (error instanceof TimeoutError) {
      console.error("SDK Timeout Error:", {
        request,
        timeout: 30000,
      });

      throw new SDKAPIError(
        "Request timed out",
        408,
        {
          timeout: 30000,
          method: request.method,
          namespace: request.namespace,
        },
        error,
      );
    }

    if (error instanceof SDKAPIError) {
      throw error;
    }

    // Network or other errors
    console.error("SDK Network Error:", {
      error,
      request,
      stack: error instanceof Error ? error.stack : undefined,
    });

    throw new SDKAPIError(
      error instanceof Error ? error.message : "Failed to call SDK proxy",
      undefined,
      {
        method: request.method,
        namespace: request.namespace,
        errorType: error instanceof Error ? error.constructor.name : "Unknown",
      },
      error,
    );
  }
}

/**
 * Custom namespace interface for our extended SDK functionality
 */
interface CustomSDKNamespace {
  getAssetPrice(symbol: string): Promise<GetAssetPriceResponse>;
}

/**
 * Extended SDK interface that includes both the original BdSdk and our custom namespace
 */
type BlockdaemonSDK = BdSdk & {
  custom: CustomSDKNamespace;
};

/**
 * Client-side SDK interface that dynamically mirrors the server-side SDK
 * All calls are automatically proxied through Cloudflare Functions with request deduplication
 */
export const blockdaemonSDK = new Proxy(
  {},
  {
    get(target, prop, receiver) {
      if (typeof prop === "string") {
        // Check if this might be a namespace by trying to access it as an object
        // We'll create a namespace proxy for any property accessed
        // The server will determine if it's a valid namespace or direct method
        return new Proxy(
          // Also make this callable as a direct method
          async (...args: unknown[]) => {
            return callSDK({
              method: prop,
              args,
            });
          },
          {
            get(target, method, receiver) {
              if (typeof method === "string") {
                return async (...args: unknown[]) => {
                  return callSDK({
                    method,
                    namespace: prop,
                    args,
                  });
                };
              }
              return Reflect.get(target, method, receiver);
            },
          },
        );
      }

      return Reflect.get(target, prop, receiver);
    },
  },
) as BlockdaemonSDK;
